import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService, SessionUser } from './auth.service';

export interface CustomImportDto {
  ID: string;
  Name: string;
  OwnerEntityID: string;
  ExportIDType: string;
  ExportIDTypeName?: string;
  CreationTime: string;
  StorageFilePath?: string;
  FileName?: string;
  ExportIDCount?: number;
  TNCIDsCount?: number;
  Status: number;
}

export interface ExportIdTypeDto {
  ExportIDType: string;
  Name: string;
}

/**
 * Canonical display label for an export ID type code, used consistently by the list rows and the
 * form dropdown so casing never diverges (e.g. always "MAID", never "maid"). Known codes get a
 * friendly label; anything else falls back to the upper-cased code.
 */
export function formatExportIdTypeLabel(exportIdType: string | null | undefined): string {
  const labelMap: Record<string, string> = {
    tncid: 'TNCiD',
    maid: 'MAID',
    email_md5: 'Indirizzi email con hashing MD5',
    email_sha256: 'Indirizzi email con hashing SHA256',
    vat_id: 'Partite IVA con hashing SHA256'
  };
  const normalized = (exportIdType ?? '').toLowerCase();
  return labelMap[normalized] || (exportIdType ?? '').toUpperCase();
}

export interface CustomImportPayload {
  ID?: string;
  Name: string;
  Owner: {
    EntityType: string;
    EntityID: string;
  };
  ExportIDType: string;
  FileName?: string;
  Status: number;
}

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CustomImportApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);
  private readonly chunkSize = 5 * 1024 * 1024;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getCustomImports(options?: { skipDcr?: boolean }): Observable<CustomImportDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/custom-import');
    return this.http.get<GenericResponse<CustomImportDto[]>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote, options)
      }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[custom-import] list failed', error);
        return throwError(() => error);
      })
    );
  }

  getExportIdTypes(): Observable<ExportIdTypeDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/exportIds');
    return this.http.get<GenericResponse<ExportIdTypeDto[]>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote)
      }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[custom-import] export ids failed', error);
        return throwError(() => error);
      })
    );
  }

  saveCustomImport(payload: CustomImportPayload, isUpdate = false): Observable<CustomImportDto> {
    const endpoint = this.composeEndpoint('/webapp/crud/custom-import');
    const method = isUpdate ? 'put' : 'post';
    const request$ = method === 'put'
      ? this.http.put<GenericResponse<CustomImportDto | CustomImportDto[]>>(endpoint.url, payload, { headers: this.buildHeaders(endpoint.useRemote) })
      : this.http.post<GenericResponse<CustomImportDto | CustomImportDto[]>>(endpoint.url, payload, { headers: this.buildHeaders(endpoint.useRemote) });

    return request$.pipe(
      map(res => {
        if (!res.payload) {
          throw new Error('Empty response from custom import save');
        }
        // Handle both array and single object response
        const data = Array.isArray(res.payload) ? res.payload[0] : res.payload;
        if (!data || !data.ID) {
          throw new Error('Invalid custom import data returned from server');
        }
        return data;
      }),
      catchError(error => {
        console.error('[custom-import] save failed', error);
        return throwError(() => error);
      })
    );
  }

  updateStatus(id: string, status: number): Observable<CustomImportDto> {
    const endpoint = this.composeEndpoint('/webapp/crud/custom-import-status');
    const body = { ID: id, Status: status };
    return this.http.put<GenericResponse<CustomImportDto>>(
      endpoint.url,
      body,
      {
        headers: this.buildHeaders(endpoint.useRemote)
      }
    ).pipe(
      map(res => {
        if (!res.payload) {
          throw new Error('Unable to update custom import status');
        }
        return res.payload;
      }),
      catchError(error => {
        console.error('[custom-import] status update failed', error);
        return throwError(() => error);
      })
    );
  }

  async uploadFile(
    file: File,
    customImportId: string,
    onProgress?: (value: number) => void
  ): Promise<void> {
    if (!file || !customImportId) {
      throw new Error('Missing upload data');
    }
    const endpoint = this.composeEndpoint('/webapp/crud/custom-import-upload');
    const finalizeEndpoint = this.composeEndpoint('/webapp/crud/custom-import-upload-finalized');
    const totalChunks = Math.ceil(file.size / this.chunkSize);
    const fileId = `${customImportId}_${crypto.randomUUID()}`;
    const mimetype = file.type || 'text/plain';

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * this.chunkSize;
      const end = Math.min(start + this.chunkSize, file.size);
      const chunkBlob = file.slice(start, end);
      const chunk = await this.blobToBase64(chunkBlob);
      await firstValueFrom(
        this.http.post(
          endpoint.url,
          { chunk, fileId, chunkIndex, mimetype },
          { headers: this.buildHeaders(endpoint.useRemote) }
        )
      );
      onProgress?.(Math.round(((chunkIndex + 1) / totalChunks) * 100));
    }

    await firstValueFrom(
      this.http.post(
        finalizeEndpoint.url,
        { fileId, totalChunks, mimetype },
        { headers: this.buildHeaders(finalizeEndpoint.useRemote) }
      )
    );
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const base64 = result.split(',')[1] || '';
          resolve(base64);
        } else {
          reject(new Error('Unable to read file chunk'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean, options?: { skipDcr?: boolean }): HttpHeaders {
    if (useRemote) {
      const token = this.authService.getTncApiToken();
      let remoteHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
      if (token) {
        remoteHeaders = remoteHeaders.set('Authorization', token);
      }
      return remoteHeaders;
    }

    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }
}
